import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';
import { graphConnect, sugiyama } from 'd3-dag';
import { ROUTE_PATH_COLORS, getEdgeGeometry, EDGE_OFFSET_SPACING } from '../constants/routeGraphStyle';
import { EdgeWithArrow, EdgeLabelNode } from './RouteGraphPrimitives';

const NODE_MIN_WIDTH = 64;
const NODE_HEIGHT = 44;
/** Height for source/dest nodes (label only); events use NODE_HEIGHT for label + Needs + Gets. */
const NODE_HEIGHT_SINGLE_LINE = 28;
const NODE_PADDING_X = 8;
const CHAR_WIDTH_EST = 5.5;
const NODE_MAX_WIDTH = 160;
/** Larger gaps so middle circle nodes on edges don’t overlap; matches route map spacing for multi-line edges */
const HORIZONTAL_GAP = 26;
const VERTICAL_GAP = 48;
const SVG_PADDING = 10;
/** Gray for middle circle nodes so they don’t compete with edge path colors */
const EDGE_LABEL_NODE_STROKE = '#757575';
/** Node rect stroke; neutral so Needs/Planned colors stand out */
const NODE_STROKE_COLOR = '#666';
const NODE_FILL_NORMAL = '#eee';
const NODE_FILL_WARNING = '#ffcdd2'; /* darker light red when requirement or conservation warning */

/** Width from longest line only (label; for events, Needs and Gets on separate lines). */
function nodeWidthForLabels(locationLabel: string, isEvent: boolean, requirement: number, quantity: number): number {
    const line1 = locationLabel.length;
    const line2 = isEvent ? `Needs: ${requirement}`.length : 0;
    const line3 = isEvent ? `Gets: ${quantity}`.length : 0;
    const maxLen = Math.max(line1, line2, line3);
    const w = NODE_PADDING_X * 2 + maxLen * CHAR_WIDTH_EST;
    return Math.min(NODE_MAX_WIDTH, Math.max(NODE_MIN_WIDTH, Math.ceil(w)));
}

export interface DagNodeRow {
    index: number;
    kind: 'source' | 'event' | 'dest';
    eventId?: string | null;
    storageLocationId?: string | null;
}

/** Per-edge list of trailer path indices (0, 1, …) so we draw one line per trailer with route colors. */
export type EdgesWithTrailerPathIndices = Array<{
    fromNodeIndex: number;
    toNodeIndex: number;
    pathIndices: number[];
}>;

export interface SupplyFlowGraphProps {
    dagNodes: DagNodeRow[];
    dagEdges: Array<{ fromNodeIndex: number; toNodeIndex: number }>;
    /** Quantity at each node (computed from flows). */
    nodeQuantities: number[];
    /** Requirement at each node (packing list); only event nodes have > 0. Used for "Needs: X". */
    nodeRequirements?: number[];
    /** Flow value per edge (for midpoint circle label). */
    edgeFlows: Array<{ fromNodeIndex: number; toNodeIndex: number; value: number }>;
    /** When set, draw multiple edge lines with route colors and offsets to match route map. */
    edgesWithTrailerPathIndices?: EdgesWithTrailerPathIndices;
    /** Node indices that have a requirement or conservation warning; use light red background. */
    nodeWarningIndices?: number[];
    getNodeLabel: (nodeIndex: number) => string;
}

/**
 * DAG visualization for supply flow: nodes show location + Needs/Gets; edges show flow quantity in circle.
 * Layout uses d3-dag sugiyama; spacing increased for middle circle nodes on edges.
 */
export default function SupplyFlowGraph({
    dagNodes,
    dagEdges,
    nodeQuantities,
    nodeRequirements = [],
    edgeFlows,
    edgesWithTrailerPathIndices,
    nodeWarningIndices = [],
    getNodeLabel,
}: SupplyFlowGraphProps) {
    const edgeFlowMap = useMemo(() => {
        const m = new Map<string, number>();
        for (const f of edgeFlows) {
            m.set(`${f.fromNodeIndex}-${f.toNodeIndex}`, f.value);
        }
        return m;
    }, [edgeFlows]);

    const nodes = useMemo(
        () =>
            dagNodes.map(n => {
                const quantity = nodeQuantities[n.index] ?? 0;
                const requirement = nodeRequirements[n.index] ?? 0;
                const isEvent = n.kind === 'event';
                return {
                    id: String(n.index),
                    label: getNodeLabel(n.index),
                    quantity,
                    requirement,
                    isEvent,
                };
            }),
        [dagNodes, nodeQuantities, nodeRequirements, getNodeLabel]
    );

    const nodeSizes = useMemo(() => {
        const map = new Map<string, { width: number; height: number }>();
        for (const n of nodes) {
            map.set(n.id, {
                width: nodeWidthForLabels(n.label, n.isEvent, n.requirement, n.quantity),
                height: n.isEvent ? NODE_HEIGHT : NODE_HEIGHT_SINGLE_LINE,
            });
        }
        return map;
    }, [nodes]);

    const nodePositions = useMemo(() => {
        if (dagNodes.length === 0) return [];
        const baseX = SVG_PADDING + NODE_MAX_WIDTH / 2;
        const baseY = SVG_PADDING + NODE_HEIGHT / 2;
        const defaultSize = { width: NODE_MIN_WIDTH, height: NODE_HEIGHT };
        const getSize = (id: string) => nodeSizes.get(id) ?? defaultSize;

        const links: Array<[string, string]> = dagEdges.map(e => [String(e.fromNodeIndex), String(e.toNodeIndex)]);
        if (links.length === 0) {
            let x = baseX;
            return nodes.map(n => {
                const size = getSize(n.id);
                const cx = x + size.width / 2;
                x += size.width + HORIZONTAL_GAP;
                return { id: n.id, x: cx, y: baseY, width: size.width, height: size.height };
            });
        }

        try {
            const connect = graphConnect();
            const dag = connect(links);
            const layout = sugiyama()
                .nodeSize((node: { data: string }) => {
                    const size = getSize(node.data);
                    return [size.width, size.height];
                })
                .gap([HORIZONTAL_GAP, VERTICAL_GAP]);
            layout(dag);

            const positions: Array<{ id: string; x: number; y: number; width: number; height: number }> = [];
            Array.from(dag.nodes()).forEach((node: { data: string; x: number; y: number }) => {
                const size = getSize(node.data);
                positions.push({
                    id: node.data,
                    x: baseX + node.x,
                    y: baseY + node.y,
                    width: size.width,
                    height: size.height,
                });
            });
            return positions;
        } catch {
            let x = baseX;
            return nodes.map(n => {
                const size = getSize(n.id);
                const cx = x + size.width / 2;
                x += size.width + HORIZONTAL_GAP;
                return { id: n.id, x: cx, y: baseY, width: size.width, height: size.height };
            });
        }
    }, [dagNodes, dagEdges, nodes, nodeSizes]);

    const width = useMemo(() => {
        if (nodePositions.length === 0) return 320;
        const maxRight = Math.max(...nodePositions.map(p => p.x + p.width / 2));
        return Math.max(320, maxRight + SVG_PADDING);
    }, [nodePositions]);

    const height = useMemo(() => {
        if (nodePositions.length === 0) return 120;
        const maxBottom = Math.max(...nodePositions.map(p => p.y + p.height / 2));
        return maxBottom + SVG_PADDING;
    }, [nodePositions]);

    const nodeById = useMemo(() => new Map(nodes.map(n => [n.id, n])), [nodes]);

    /** One entry per drawn line: multiple lines per edge when edgesWithTrailerPathIndices has pathIndices. */
    const edgeDrawList = useMemo(() => {
        const byId = new Map(nodePositions.map(p => [p.id, p]));
        const list: Array<{
            key: string;
            value: number;
            color: string;
            geom: ReturnType<typeof getEdgeGeometry>;
            labelCx: number;
            labelCy: number;
        }> = [];
        dagEdges.forEach((edge, edgeIdx) => {
            const fromPos = byId.get(String(edge.fromNodeIndex));
            const toPos = byId.get(String(edge.toNodeIndex));
            if (!fromPos || !toPos) return;
            const key = `${edge.fromNodeIndex}-${edge.toNodeIndex}`;
            const value = edgeFlowMap.get(key) ?? 0;
            const pathIndices =
                edgesWithTrailerPathIndices?.find(e => e.fromNodeIndex === edge.fromNodeIndex && e.toNodeIndex === edge.toNodeIndex)
                    ?.pathIndices ?? [];
            const n = pathIndices.length > 0 ? pathIndices.length : 1;
            const pathIndicesToDraw = pathIndices.length > 0 ? pathIndices : [edgeIdx];
            const baseGeom = getEdgeGeometry(fromPos.x, fromPos.y, toPos.x, toPos.y, 0.5, 0);
            pathIndicesToDraw.forEach((pathIndex, i) => {
                const offset = n === 1 ? 0 : (i - (n - 1) / 2) * EDGE_OFFSET_SPACING;
                const geom = getEdgeGeometry(fromPos.x, fromPos.y, toPos.x, toPos.y, 0.5, offset);
                const color = ROUTE_PATH_COLORS[pathIndex % ROUTE_PATH_COLORS.length];
                list.push({
                    key: n > 1 ? `${key}-${pathIndex}` : key,
                    value,
                    color,
                    geom,
                    labelCx: baseGeom.midX,
                    labelCy: baseGeom.midY,
                });
            });
        });
        return list;
    }, [dagEdges, nodePositions, edgeFlowMap, edgesWithTrailerPathIndices]);

    /** One label circle per DAG edge (shared midpoint for multi-line edges) */
    const edgeLabels = useMemo(() => {
        const byKey = new Map<string, { key: string; value: number; midX: number; midY: number }>();
        dagEdges.forEach(edge => {
            const key = `${edge.fromNodeIndex}-${edge.toNodeIndex}`;
            if (byKey.has(key)) return;
            const value = edgeFlowMap.get(key) ?? 0;
            const first = edgeDrawList.find(e => e.key === key || e.key.startsWith(`${key}-`));
            if (first) {
                byKey.set(key, { key, value, midX: first.labelCx, midY: first.labelCy });
            }
        });
        return Array.from(byKey.values());
    }, [dagEdges, edgeFlowMap, edgeDrawList]);

    if (nodes.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                No nodes to display.
            </Typography>
        );
    }

    return (
        <Box sx={{ overflowX: 'auto', overflowY: 'visible', width: '100%', minWidth: 0 }}>
            <Box sx={{ minWidth: width }}>
                <svg width={width} height={height} style={{ display: 'block' }}>
                    {/* 1. Edges – one line per trailer with route colors and offset */}
                    {edgeDrawList.map(({ key, color, geom }) => (
                        <EdgeWithArrow key={key} geometry={geom} color={color} pointerEvents="none" />
                    ))}

                    {/* 2. Nodes – on top of edges so rects don’t cover arrows */}
                    {nodePositions.map(({ id, x, y, width: w, height: h }) => {
                        const node = nodeById.get(id);
                        const label = node?.label ?? '';
                        const quantity = node?.quantity ?? 0;
                        const requirement = node?.requirement ?? 0;
                        const isEvent = node?.isEvent ?? false;
                        const rx = x - w / 2;
                        const ry = y - h / 2;
                        const hasWarning = nodeWarningIndices.includes(Number(id));

                        return (
                            <g key={id}>
                                <rect
                                    x={rx}
                                    y={ry}
                                    width={w}
                                    height={h}
                                    rx={4}
                                    ry={4}
                                    fill={hasWarning ? NODE_FILL_WARNING : NODE_FILL_NORMAL}
                                    stroke={NODE_STROKE_COLOR}
                                    strokeWidth={2}
                                />
                                <text
                                    x={x}
                                    y={isEvent ? y - 10 : y}
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    fontSize={10}
                                    fontWeight={600}
                                    fill="#333"
                                >
                                    {label}
                                </text>
                                {isEvent ? (
                                    <>
                                        <text x={x} y={y + 2} textAnchor="middle" dominantBaseline="middle" fontSize={10} fill="#1565c0">
                                            Needs: {requirement}
                                        </text>
                                        <text
                                            x={x}
                                            y={y + 14}
                                            textAnchor="middle"
                                            dominantBaseline="middle"
                                            fontSize={10}
                                            fontWeight={600}
                                            fill="#2e7d32"
                                        >
                                            Gets: {quantity}
                                        </text>
                                    </>
                                ) : null}
                            </g>
                        );
                    })}

                    {/* 3. Edge label nodes – gray circle at midpoint with flow quantity */}
                    {edgeLabels.map(({ key, value, midX, midY }) => (
                        <EdgeLabelNode key={`label-${key}`} cx={midX} cy={midY} label={value} color={EDGE_LABEL_NODE_STROKE} />
                    ))}
                </svg>
            </Box>
        </Box>
    );
}
